import { caseDetailSpan } from 'utils'

export type AddressFields = {
  city?: string | null
  state?: string | null
  postal?: string | null
}

export type AddressOptions = {
  useCaseDetailSpan?: boolean
  keyPrefix?: string
  postalFieldName?: string
}

/**
 * Formats city, state, and postal code with smart comma placement
 * Groups state and postal code together (e.g., "IL 60601" not "IL, 60601")
 * Only includes fields that have values
 *
 * @param fields - Object containing city, state, and postal fields
 * @param options - Optional caseDetailSpan configuration
 * @returns Formatted address string with smart comma placement
 *
 * @example
 * formatCityStatePostal({ city: "Chicago", state: "IL", postal: "60601" })
 * // Returns: "Chicago, IL 60601"
 *
 * @example
 * formatCityStatePostal(
 *   { city: "Chicago", state: "IL", postal: "60601" },
 *   { useCaseDetailSpan: true, keyPrefix: "funeralHomeDetails", postalFieldName: "zip_code" }
 * )
 * // Returns: caseDetailSpan-wrapped values with smart comma placement
 */
export const formatCityStatePostal = (
  fields: AddressFields,
  options?: AddressOptions,
): string => {
  const { city, state, postal } = fields
  const {
    useCaseDetailSpan = false,
    keyPrefix = '',
    postalFieldName = 'postal',
  } = options || {}

  if (!useCaseDetailSpan) {
    // Original logic for plain text
    const parts: string[] = []

    if (city?.trim?.()) {
      parts.push(city.trim())
    }

    if (state?.trim?.() && postal?.trim?.()) {
      parts.push(`${state.trim()} ${postal.trim()}`)
    } else if (state?.trim?.()) {
      parts.push(state.trim())
    } else if (postal?.trim?.()) {
      parts.push(postal.trim())
    }

    return parts.join(', ')
  }

  // New logic for caseDetailSpan wrapping
  const parts: string[] = []
  const cityKey = keyPrefix ? `${keyPrefix}.city` : 'city'
  const stateKey = keyPrefix ? `${keyPrefix}.state` : 'state'
  const postalKey = keyPrefix
    ? `${keyPrefix}.${postalFieldName}`
    : postalFieldName

  if (city?.trim?.()) {
    parts.push(caseDetailSpan(cityKey))
  }

  if (state?.trim?.() && postal?.trim?.()) {
    parts.push(`${caseDetailSpan(stateKey)} ${caseDetailSpan(postalKey)}`)
  } else if (state?.trim?.()) {
    parts.push(caseDetailSpan(stateKey))
  } else if (postal?.trim?.()) {
    parts.push(caseDetailSpan(postalKey))
  }

  return parts.join(', ')
}
